/**
 * Recording-quality notice shown on practice results (Manual + Automatic) in
 * place of pronunciation feedback. Two flavours, so styling + wording live in
 * ONE place:
 *  - Cut off (a real but partial score, > 0): normal feedback, not an error —
 *    same neutral styling as the pronunciation feedback box. In Automatic the
 *    fix is to slow the speed down; in Manual, to finish speaking before
 *    stopping.
 *  - Mic issue (score 0 = no speech, or null = too quiet): rose error, showing
 *    the backend's message.
 *
 * Props:
 *  - audioIssue: the backend audio_issue string (notice hidden when empty)
 *  - score: overall_score for this attempt (0 / null / a number)
 *  - mode: 'automatic' | 'manual' — tailors the cut-off advice
 */
// A cut-off recording comes back with no score at all (19 Oct 2026), so it is
// recognised by the server's own wording as well as by a score.
const isCutOff = (audioIssue, score) => !!audioIssue && ((score != null && score > 0)
    || /not recorded|only part of the phrase|stopped before the end/i.test(audioIssue));

// What to do about it, for the part that was lost and the mode they are in.
// One copy for the result card and the hands-free score flash.
export const cutOffText = (audioIssue, mode) => {
    const lostStart = /start of the phrase/i.test(audioIssue || '');
    const what = /not recorded|only part of the phrase/i.test(audioIssue || '')
        ? audioIssue
        : 'It sounds like the recording stopped before you finished.';
    const advice = lostStart
        ? (mode === 'automatic' ? 'Wait until "Speak now" shows before you start.'
                                : 'Start speaking just after you tap record.')
        : (mode === 'automatic' ? 'Try lowering the speed so you can keep up, or check your mic.'
                                : 'Try to say the whole phrase before you stop, or check your mic.');
    return `${what} ${advice}`;
};

export default function RecordingNotice({ audioIssue, score, mode }) {
    if (!audioIssue) return null;

    if (isCutOff(audioIssue, score)) {
        return (
            <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200" data-testid="recording-notice-cutoff">
                <p className="text-sm font-semibold text-amber-700 mb-1">Recording cut off</p>
                <p className="text-xs text-amber-900">{cutOffText(audioIssue, mode)}</p>
            </div>
        );
    }

    return (
        <div className="mb-6 p-4 bg-rose-50 rounded-xl border border-rose-200">
            <p className="text-sm font-medium text-rose-600 mb-1">Recording issue</p>
            <p className="text-xs text-rose-700">{audioIssue}</p>
        </div>
    );
}

/**
 * Compact line under the score number in Automatic's transient score flash.
 * Returns { text, className } or null. Same rule as the notice above: cut off
 * (score > 0) is neutral feedback with speed advice; a mic issue (score 0/null)
 * is the rose backend message; otherwise the grey pronunciation feedback.
 */
export function scoreFlashNotice({ score, audioIssue, feedback, scoreLocked }) {
    // No score was produced: past the free allowance nothing is sent to the
    // scoring engines. Says what upgrading buys rather than implying a number
    // exists behind the padlock.
    // The caption under the padlock carries it now, with the link.
    if (scoreLocked) return null;
    if (isCutOff(audioIssue, score)) return { text: cutOffText(audioIssue, 'automatic'), className: 'text-stone-500' };
    if (audioIssue) return { text: audioIssue, className: 'text-rose-600 font-medium' };
    if (feedback) return { text: feedback, className: 'text-stone-500' };
    return null;
}
